/**
 * The three controls every task carries: what to DO about the item, "Później", and "Gotowe".
 *
 * One component for the Pulpit's two cards and the client's Przegląd. The three are laid out
 * differently, but the cluster is identical, and two copies of it would be two places to get the
 * accessible description and the response payload subtly different. Promoted out of
 * `pages/trainer-home` on 24 September 2026, when the Przegląd's Talia became its second surface.
 *
 * ── EVERY control here carries the person, and none of them carries a `title` ────────────────
 * Five rows of "Gotowe" read out in sequence say nothing about which task is being cleared, so each
 * control's accessible name is its own visible word followed by the person, composed through one
 * key. The name CONTAINS the visible word, which is what lets a speech-control user say what they
 * can see.
 *
 * `title` on an element that already has an `aria-label` becomes its accessible DESCRIPTION, so the
 * reason glyph announced its reason twice and lost its `title` for good. The two ICON-ONLY buttons
 * keep theirs: nothing on them is drawn as words, and the pointer user has no other reading of
 * "Później" and "Gotowe".
 */

import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { CheckIcon } from '~/shared/ui/AuthIcons';
import { ElapsedMarkIcon } from '~/shared/ui/EmptyStateIcons';
import ink from '~/shared/ui/inkButton.module.css';

import type { AttentionResponseKind } from '../lib/useAttentionResponse';
import { QUEUE_CONTROL_ATTRIBUTE } from '../lib/queueMarks';
import { primaryNaming, taskLabel } from '../lib/taskLabel';
import type { AttentionItem } from '../model/attentionItems';
import { clipDestination } from '../model/taskAction';
import { ActGlyphMark, actionGlyph } from './TaskActs';
import styles from './TaskControls.module.css';

interface TaskControlsProps {
  readonly item: AttentionItem;
  /**
   * Whether this row's note composer is open.
   *
   * Always `false` for every other action kind, and the card is what knows: one row at a time is
   * open per card, which is `lib/useQueueCard`'s single slot.
   */
  readonly expanded: boolean;
  /**
   * The primary was pressed, and the primary is a BUTTON rather than a link.
   *
   * Two kinds take it — the note toggle and "Oznacz jako przejrzane" — and what each of them does
   * is the card's decision, because one of them writes and the write belongs where the focus
   * landing and the live region are.
   */
  readonly onPrimary: () => void;
  /**
   * Record one of the two answers. The CARD supplies it, because clearing a row moves focus and
   * only the card outlives the row — see `lib/useQueueCard`.
   */
  readonly onAnswer: (kind: AttentionResponseKind) => void;
  /**
   * The primary at the row's START and the two responses at its end — the foot of a "Szybki
   * feedback" card (KANON "Karty zadań", 24 September 2026), which spans the card. The task rows
   * keep the one cluster at their right edge.
   *
   * A card's foot also draws the primary's glyph beside its word, as the `/zadania` desk does
   * (owner, 25 September 2026). The Pulpit's task rows stay words only: a row has no room to spare.
   */
  readonly spread?: boolean;
  /** What stands beside the primary in the spread foot — the way into the whole session. */
  readonly beside?: ReactNode;
  /**
   * A primary the host draws itself, in place of the one built from `item.action`.
   *
   * The Przegląd's session task: its „Skomentuj sesję" opens the session panel over the tab rather
   * than a note under the card, so it is a plain button carrying the screen's focus-return address
   * (`data-session-opener`), which only that screen knows how to spell. It is handed the primary's
   * skin and the act's glyph (`null` off a card's foot), so it reads as the same control.
   */
  readonly primary?: (skin: string, glyph: ReactNode) => ReactNode;
}

export function TaskControls({
  item,
  expanded,
  onPrimary,
  onAnswer,
  spread = false,
  beside,
  primary: hostPrimary,
}: TaskControlsProps) {
  const { t } = useTranslation();

  // The person, for a control whose visible word is the same on every row.
  const task = taskLabel(item, t);
  // A record's „Pogratuluj" wears the chat's bubble on a row as well: the word alone does not say
  // that it leads to a conversation (`docs/design/zadania-progi-2026-10-02/`, view 4).
  const glyph =
    spread || item.reason === 'prAchieved' ? <ActGlyphMark glyph={actionGlyph(item.action)} /> : null;

  return (
    <span className={`${String(styles.controls)} ${spread ? String(styles.spread) : ''}`}>
      <span className={styles.cluster}>
        {hostPrimary === undefined ? primary() : hostPrimary(`${ink.ink} ${String(styles.primary)}`, glyph)}
        {spread && beside != null ? <span className={styles.beside}>{beside}</span> : null}
        {spread ? <span className={styles.spacer} /> : null}
        <button
          className={styles.iconButton}
          type="button"
          {...{ [QUEUE_CONTROL_ATTRIBUTE]: 'Snoozed' }}
          aria-label={t('web:home.action.snoozeFor', { task })}
          // Icon-only: the `title` is the pointer user's only reading of the word. It becomes the
          // accessible DESCRIPTION, and repeating the visible word after the name is acceptable.
          title={t('web:home.action.snoozeTitle')}
          onClick={() => {
            onAnswer('Snoozed');
          }}
        >
          {/* A clock: the task comes back rather than going away. */}
          <ElapsedMarkIcon className={styles.iconMark} />
        </button>
        <button
          className={styles.iconButton}
          type="button"
          {...{ [QUEUE_CONTROL_ATTRIBUTE]: 'Done' }}
          aria-label={t('web:home.action.doneFor', { task })}
          title={t('web:home.action.doneTitle')}
          onClick={() => {
            onAnswer('Done');
          }}
        >
          <CheckIcon className={styles.iconMark} />
        </button>
      </span>
    </span>
  );

  function primary() {
    // Read off what is ACTUALLY on the control: the note toggle's word changes while its row is
    // open, and a name still saying "Skomentuj" over a button reading "Zwiń komentarz" is a control
    // a speech-control user cannot address.
    const label =
      expanded && item.action.kind === 'sessionNote'
        ? t('web:home.action.closeNote')
        : t(item.action.labelKey);
    const { name, hint } = primaryNaming(item, label, t);

    switch (item.action.kind) {
      // The two kinds whose primary is a BUTTON. The note expands its row and says so; the review
      // writes and stays where it is, so it carries no `aria-expanded` at all rather than one
      // permanently reading "collapsed".
      case 'sessionNote':
      case 'markReviewed':
        return (
          <button
            className={`${ink.ink} ${styles.primary}`}
            type="button"
            aria-label={name}
            aria-expanded={item.action.kind === 'markReviewed' ? undefined : expanded}
            onClick={onPrimary}
          >
            {glyph}
            {label}
          </button>
        );
      case 'openClip':
        return (
          <Link
            className={`${ink.ink} ${styles.primary}`}
            {...clipDestination(item.action)}
            aria-label={name}
          >
            {glyph}
            {label}
          </Link>
        );
      case 'openPlans':
        return (
          <Link className={`${ink.ink} ${styles.primary}`} to="/plans" aria-label={name}>
            {glyph}
            {label}
          </Link>
        );
      case 'openClient':
        return (
          <Link
            className={`${ink.ink} ${styles.primary}`}
            to="/klienci/$clientId"
            params={{ clientId: String(item.action.clientId) }}
            aria-label={name}
          >
            {glyph}
            {label}
          </Link>
        );
      case 'openRoster':
        return (
          <Link className={`${ink.ink} ${styles.primary}`} to="/klienci" aria-label={name}>
            {glyph}
            {label}
          </Link>
        );
      case 'openChat':
        return (
          <Link
            className={`${ink.ink} ${styles.primary}`}
            to="/czat"
            search={item.subjectCustomerId === null ? {} : { klient: item.subjectCustomerId }}
            aria-label={name}
            title={hint}
          >
            {glyph}
            {label}
          </Link>
        );
      case 'openLead':
        return item.action.leadId === null ? (
          <Link className={`${ink.ink} ${styles.primary}`} to="/klienci/zgloszenia" aria-label={name}>
            {glyph}
            {label}
          </Link>
        ) : (
          <Link
            className={`${ink.ink} ${styles.primary}`}
            to="/klienci/zgloszenia/$leadId"
            params={{ leadId: String(item.action.leadId) }}
            aria-label={name}
          >
            {glyph}
            {label}
          </Link>
        );
    }
  }
}
